<template>
    <div>
        <b-card class="bg-default-card no-padding" v-if="trajectory_enrolment">
            <b-tabs card>
                <b-tab :title="uLang('app.entity_details', { entity: lang('app.trajectory_enrolment') })" active>
                    <div class="row enrolment-status" v-if="$hasRole('trajectory_inspector') || $hasRole('super_admin')">
                        <div class="col-md-10 my-auto">
                            <backend-select
                                name="trajectory_enrolment_status_edit"
                                :route="route('trajectory-enrolment-statuses.index')"
                                object-name-key="translated_name"
                                v-model="status"
                                :disabled="!statusChangeable"
                            />
                        </div>
                        <div class="col-md-2">
                            <button class="btn btn-block btn-brand"
                               href="#"
                               @click.prevent="updateStatus"
                               :disabled="!statusChangeable"
                            >{{ uLang('app.store_type', { type: lang('app.status') }) }}</button>
                        </div>
                    </div>

                    <div class="row">
                        <div class="col-md-12">
                            <div class="inner">
                                <div class="row">
                                    <div class="col-md-6">
                                        <table class="table custom-stripe">
                                            <tr>
                                                <td width="200">{{ uLang('app.project_name') }}: </td>
                                                <td>
                                                    <link-if-permissions permissions="view-trajectory-projects" :to="{
                                                        name: 'admin.trajectory_projects.view',
                                                        params: { id: trajectory_enrolment.trajectory_project_id }
                                                    }">{{ trajectory_enrolment.trajectory_project.name }}</link-if-permissions>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td width="200">{{ uLang('app.trajectory_name') }}: </td>
                                                <td>
                                                    <link-if-permissions permissions="view-trajectories" :to="{ name: 'admin.trajectory_projects.trajectories.view', params: { projectId: trajectory_enrolment.trajectory_project_id, id: trajectory_enrolment.trajectory_id }}">
                                                        {{ trajectory_enrolment.trajectory.name }}
                                                    </link-if-permissions>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td width="200">{{ uLang('app.trajectory_manager') }}: </td>
                                                <td>
                                                    <link-if-permissions v-if="trajectory_enrolment.trajectory_manager_id" permissions="view-relations" :to="{ name: 'admin.relations.view', params: { id: trajectory_enrolment.trajectory_manager_id }}">
                                                        {{ trajectory_enrolment.trajectory_manager.full_name }}
                                                    </link-if-permissions>
                                                    <span v-else>
                                                        -
                                                    </span>
                                                </td>
                                            </tr>
                                            <tr v-if="trajectory_enrolment.trajectory.trajectory_inspectors">
                                                <td width="200">{{ uLang('app.trajectory_inspectors') }}: </td>
                                                <td>
                                                    <b-badge class="mr-2" pill variant="primary" v-for="inspector in trajectory_enrolment.trajectory.trajectory_inspectors" :key="inspector.id">
                                                        <link-if-permissions permissions="view-relations" :to="{ name: 'admin.relations.view', params: { id: inspector.id }}">
                                                            {{ inspector.full_name }}
                                                        </link-if-permissions>
                                                    </b-badge>
                                                </td>
                                            </tr>
                                            <tr v-if="trajectory_enrolment.casemanager">
                                                <td width="200">{{ uLang('app.casemanager') }}: </td>
                                                <td>
                                                    <b-badge pill variant="primary">
                                                        <link-if-permissions permissions="view-relations" :to="{ name: 'admin.relations.view', params: { id: trajectory_enrolment.casemanager.id }}">
                                                            {{ trajectory_enrolment.casemanager.full_name }}
                                                        </link-if-permissions>
                                                    </b-badge>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td width="200">{{ uLang('app.start_date') }}: </td>
                                                <td>{{ trajectory_enrolment.trajectory_start_date | date }}</td>
                                            </tr>
                                            <tr v-if="trajectory_enrolment.trajectory_end_date > '' && this.$can('update-trajectories')">
                                                <td width="200">{{ uLang('app.end_date') }}: </td>
                                                <td>
                                                    {{ trajectory_enrolment.trajectory_end_date | date }}
                                                    <button class="btn btn-light btn-sm" @click="editEndDate">
                                                        <i class="fa fa-edit"></i> {{ uLang('app.edit') }}
                                                    </button>

                                                    <div v-if="editingEndDate" class="d-flex flex-grow justify-content-between p-1">
                                                        <vue-pikaday
                                                            v-model="end_date"
                                                            :options="pikadayOptions"
                                                            :required="true"
                                                            class="form-control"
                                                            autocomplete="off"
                                                        />
                                                        <button
                                                            class="btn btn-sm btn-brand"
                                                            href="#"
                                                            @click.prevent="updateEndDate"
                                                        >{{ uLang('app.store_type', {type: lang('app.date')}) }}
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                            <tr v-if="!$hasRole('candidate')">
                                                <td width="200">{{ uLang('app.fact_sheet') }}:</td>
                                                <td v-if="hasFactSheet">
                                                    <button
                                                        type="button"
                                                        @click="showDocument = true"
                                                        class="btn btn-brand"
                                                    >{{ uLang('app.show_pdf_in_new_tab') }}
                                                    </button>

                                                    <portal to="modals" v-if="showDocument">
                                                        <b-modal
                                                            v-model="showDocument"
                                                            :hideFooter="true"
                                                            :title="`${uLang('app.fact_sheet')}: ${trajectory_enrolment.trajectory.factsheet.name}`"
                                                            size="lg"
                                                        >
                                                            <file-preview
                                                                :url="factSheetUrl"
                                                                :height="'80vh'"
                                                            />
                                                        </b-modal>
                                                    </portal>

                                                </td>
                                                <td v-else>
                                                    {{ uLang('app.no_fact_sheet_available') }}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td width="200">{{ uLang('app.credit') }}:</td>
                                                <td>{{ trajectory_enrolment.credit }}%</td>
                                            </tr>
                                        </table>
                                    </div>
                                    <div class="col-md-6">
                                        <table class="table custom-stripe">
                                            <tr>
                                                <td width="200">{{ uLang('app.name') }}: </td>
                                                <td>
                                                    <link-if-permissions permissions="view-relations" :to="{ name: 'admin.relations.view', params: { id: trajectory_enrolment.relation_id }}">
                                                        {{ trajectory_enrolment.relation.full_name }}
                                                    </link-if-permissions>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td width="200">{{ uLang('app.enrolment_contact_client') }}: </td>
                                                <td>
                                                    <link-if-permissions permissions="view-relations" :to="{ name: 'admin.relations.view', params: { id: trajectory_enrolment.contact_id }}">
                                                        {{ trajectory_enrolment.contact.full_name }}
                                                    </link-if-permissions>
                                                </td>
                                            </tr>
                                            <tr v-if="trajectory_enrolment.reporting_person">
                                                <td width="200">{{ uLang('app.enrolment_reporting_person') }}: </td>
                                                <td>
                                                    <link-if-permissions permissions="view-relations" :to="{ name: 'admin.relations.view', params: { id: trajectory_enrolment.reporting_person_id }}">
                                                        {{ trajectory_enrolment.reporting_person.full_name }}
                                                    </link-if-permissions>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td width="200">{{ uLang('app.maximum_hours') }}: </td>
                                                <td>0</td>
                                            </tr>
                                            <tr>
                                                <td width="200">{{ uLang('app.hours_worked') }}: </td>
                                                <td>0</td>
                                            </tr>
                                            <tr>
                                                <td width="200">{{ uLang('app.remaining_hours') }}: </td>
                                                <td>0</td>
                                            </tr>
                                            <tr>
                                                <td width="200">{{ uLang('app.recruited_by') }}: </td>
                                                <td>{{ $getIfExists(trajectory_enrolment, 'receives_lead_person.full_name', 'Id plein') }}</td>
                                            </tr>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="row">
                        <div class="col-md-12 tabs-show-area">
                            <b-card no-body class="mt-3">
                                <b-tabs card>
                                    <b-tab :title="uLang('app.milestone_progress')" active>
                                        <milestone-progress-table :url="route('relations.trajectory-enrolments.milestone-progresses.table', {
                                                relation: trajectory_enrolment.relation_id,
                                                trajectory_enrolment: trajectory_enrolment.id,
                                            })" :relation="trajectory_enrolment.relation" :trajectory-enrolment="trajectory_enrolment"/>
                                    </b-tab>
                                </b-tabs>
                            </b-card>
                        </div>
                    </div>
                </b-tab>
            </b-tabs>
        </b-card>
    </div>
</template>

<script>
    import MilestoneProgressTable from '~/components/tables/admin/relation/trajectory-enrolment/milestone-progress/table'
    import { ErrorAlert, SuccessfullyUpdatedAlert } from "~/Swals";
    import { PikadayOptions } from "~/Form/elements";

    export default {
        name: 'show',

        components: {
            'milestone-progress-table': MilestoneProgressTable,
        },

        props: {
            relationId: {
                required: true,
            },
            trajectoryEnrolmentId: {
                required: true,
            },
        },

        data: function () {
            return {
                relation: null,
                trajectory_enrolment: null,
                status: null,
                end_date: null,
                editingEndDate: false,
                pikadayOptions: PikadayOptions,
                showDocument: false,
            }
        },

        computed: {
            statusChangeable() {
                return true;// $getIfExists(this.trajectory_enrolment, ['trajectory_enrolment_status', 'name']) === 'completed';
            },
            factSheetUrl() {
                return route('resource.files.show', {
                    file: this.trajectory_enrolment.trajectory.factsheet.id,
                    downloadType: 'stream'
                })
            },
            hasFactSheet() {
                return !!this.trajectory_enrolment.trajectory.factsheet
            }
        },

        created: function () {
            this.retrieveTrajectoryEnrolment();
        },

        methods: {
            async retrieveTrajectoryEnrolment() {
                const { data } = await window.axios.get(route('relations.trajectory-enrolments.show', {
                    relation: this.relationId,
                    trajectory_enrolment: this.trajectoryEnrolmentId,
                }));
                this.trajectory_enrolment = data;
                this.status = data.trajectory_enrolment_status;
                this.end_date = data.trajectory_end_date;
            },

            async updateStatus() {
                let { data } = await window.axios.put(
                    route('relations.trajectories.update-status', {
                        relation: this.relationId,
                        trajectory_enrolment: this.trajectoryEnrolmentId,
                    }), {
                        id : this.status.id
                    }
                ).then(({ data }) => {
                    new SuccessfullyUpdatedAlert({entity: this.lang('app.candidate_enrolment')})
                        .show()
                        .then(() => {
                            this.trajectory_enrolment.trajectory_end_date = data.trajectory_end_date;
                            this.$router.push({name: 'admin.relations.trajectory-enrolments.view', params: { id: this.trajectoryEnrolmentId.id }});
                        });
                }).catch(({ response }) => {
                    new ErrorAlert({text: response.data.errors.status }).show();
                });

                this.trajectory_enrolment = data;
            },
            // Update the end date
            async updateEndDate() {
                let { data } = await window.axios.put(
                    route('relations.trajectories.update-end-date', {
                        relation: this.relationId,
                        trajectory_enrolment: this.trajectoryEnrolmentId,
                    }), {
                        end_date : moment(this.end_date).format('YYYY/MM/DD')
                    }
                ).then(({ data }) => {
                    new SuccessfullyUpdatedAlert({entity: this.lang('app.candidate_enrolment')})
                    .show()
                    .then(() => {
                        this.trajectory_enrolment.trajectory_end_date = data.trajectory_end_date;
                    });
                }).catch(({ response }) => {
                    new ErrorAlert({text: response.data.errors.end_date }).show();
                }).finally(() => {
                    this.editingEndDate = false;
                })

                this.trajectory_enrolment = data;
            },
            editEndDate() {
                this.editingEndDate = !this.editingEndDate;
            }
        }
    }
</script>




<style lang="scss" scoped>
    .relation_image {
        max-width: 100%;
        max-height: 350px;
    }
    .detail-buttons {
        margin-bottom: 15px;
        float: right;
    }
    .add_address {
        margin-top: 15px;
        margin-bottom: 15px;
    }
    .action_icon {
        cursor: pointer;
        margin-right: 5px;
    }
    .address_block a{
        margin-right: 10px;
    }

    .enrolment-status {
        background-color: #f3f1f6;
        padding: 8px 15px;
        border: 1px solid #eceeef;
        p {
            margin: 0;
            line-height: 20px;
            font-weight: 500;
            letter-spacing: 0px;
        }
        .row {
            margin-bottom: 0;
        }
        .btn {
            line-height: inherit;
        }
    }
</style>
